import { getAuth } from 'firebase/auth';
import type {
  CliCandidatePrepareData,
  CliCandidatePrepareRequest,
  CliCandidateStatusData,
  CliProductInsightLineageData,
} from '@modules/shared-types';
import type {
  ExperimentAuditEntry,
  ExperimentDocument,
  ExperimentInputPayload,
  ExperimentTransitionPayload,
  ResearchReport,
  SegmentResponseMapData,
} from '../experimentTypes';
import {
  parseAdvancedReadinessRecord,
  type AdvancedReadinessSafeRecord,
} from '../advancedReadiness';
import {
  parseResearchCycleProductPreflight,
  parseResearchCycleProductState,
  parseResearchCycleStrategyReuseProjection,
  type ResearchCycleProductPreflight,
  type ResearchCycleProductState,
  type ResearchCycleStrategyReuseProjection,
  type ResearchCycleStrategyReuseRequest,
} from '../researchCycle';

function getBaseUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname === 'dev.segmently.ai' || hostname.includes('devsegmentlyai')) {
    return 'https://dev-api.segmently.ai';
  }
  if (hostname === 'stage.segmently.ai' || hostname.includes('stagesegmentlyai')) {
    return 'https://stage-api.segmently.ai';
  }
  return 'https://api.segmently.ai';
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuth().currentUser?.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

interface Envelope<T> {
  apiVersion: string;
  asOf: string;
  data: T;
  warnings: Array<{ code: string; message: string }>;
}

/** Problem+json failure with the machine-readable `code` preserved (e.g. 'candidate_stale'). */
export class ExperimentsApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'ExperimentsApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<Envelope<T>> {
  const text = await response.text().catch(() => '');
  let payload: Envelope<T> | { detail?: string; title?: string; code?: string } | undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON response from experiments API: HTTP ${response.status}`);
    }
  }

  if (!response.ok) {
    let message: string | undefined;
    let code: string | undefined;
    if (payload && 'detail' in payload) message = payload.detail;
    else if (payload && 'title' in payload) message = payload.title;
    if (payload && 'code' in payload) code = payload.code;
    throw new ExperimentsApiError(message || `HTTP ${response.status}`, response.status, code);
  }

  return payload as Envelope<T>;
}

async function handleResearchCycleResponse(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '');
  let payload: { success?: unknown; data?: unknown; message?: unknown; detail?: unknown; code?: unknown } | null = null;
  if (text) {
    try { payload = JSON.parse(text); } catch { throw new Error(`Invalid JSON response from Research Cycle API: HTTP ${response.status}`); }
  }
  if (!response.ok || payload?.success !== true) {
    const message = typeof payload?.message === 'string'
      ? payload.message
      : typeof payload?.detail === 'string'
        ? payload.detail
        : `HTTP ${response.status}`;
    throw new ExperimentsApiError(
      message,
      response.status,
      typeof payload?.code === 'string' ? payload.code : undefined,
    );
  }
  return payload.data;
}

export class ExperimentsApiClient {
  constructor(private readonly baseUrl = getBaseUrl()) {}

  private url(projectId: string, suffix = ''): string {
    return `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/insights/v1/experiments${suffix}`;
  }

  private researchCycleUrl(projectId: string, suffix = ''): string {
    return `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/insights/v1/research-cycles/launch${suffix}`;
  }

  async preflightResearchCycle(
    projectId: string,
    reportId: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<ResearchCycleProductPreflight> {
    const response = await fetch(this.researchCycleUrl(projectId, '/preflight'), {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ reportId }),
      signal,
    });
    return parseResearchCycleProductPreflight(await handleResearchCycleResponse(response), { projectId, reportId });
  }

  async startResearchCycle(
    projectId: string,
    reportId: string,
    idempotencyKey: string,
  ): Promise<ResearchCycleProductState> {
    const response = await fetch(this.researchCycleUrl(projectId), {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ reportId }),
    });
    return parseResearchCycleProductState(await handleResearchCycleResponse(response), { projectId, reportId });
  }

  async getResearchCycle(
    projectId: string,
    runId: string,
    signal?: AbortSignal,
  ): Promise<ResearchCycleProductState> {
    const response = await fetch(this.researchCycleUrl(projectId, `/${encodeURIComponent(runId)}`), {
      headers: await getAuthHeaders(),
      signal,
    });
    return parseResearchCycleProductState(await handleResearchCycleResponse(response), { projectId, runId });
  }

  async retryResearchCycle(
    projectId: string,
    runId: string,
    revision: number,
    idempotencyKey: string,
  ): Promise<ResearchCycleProductState> {
    const response = await fetch(this.researchCycleUrl(projectId, `/${encodeURIComponent(runId)}/retry`), {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Idempotency-Key': idempotencyKey,
        'If-Match': String(revision),
      },
      body: JSON.stringify({}),
    });
    return parseResearchCycleProductState(await handleResearchCycleResponse(response), { projectId, runId });
  }

  async preflightResearchCycleReuse(
    projectId: string,
    runId: string,
    input: ResearchCycleStrategyReuseRequest,
    signal?: AbortSignal,
  ): Promise<ResearchCycleStrategyReuseProjection> {
    const response = await fetch(this.researchCycleUrl(projectId, `/${encodeURIComponent(runId)}/reuse/preflight`), {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
      signal,
    });
    return parseResearchCycleStrategyReuseProjection(await handleResearchCycleResponse(response), {
      runId,
      strategyId: input.strategyId,
      strategyVersionId: input.strategyVersionId,
      releaseId: input.releaseId,
    });
  }

  async reuseResearchCycleResult(
    projectId: string,
    runId: string,
    input: ResearchCycleStrategyReuseRequest,
    revision: number,
    idempotencyKey: string,
  ): Promise<ResearchCycleStrategyReuseProjection> {
    const response = await fetch(this.researchCycleUrl(projectId, `/${encodeURIComponent(runId)}/reuse`), {
      method: 'POST',
      headers: {
        ...(await getAuthHeaders()),
        'Idempotency-Key': idempotencyKey,
        'If-Match': String(revision),
      },
      body: JSON.stringify(input),
    });
    return parseResearchCycleStrategyReuseProjection(await handleResearchCycleResponse(response), {
      runId,
      strategyId: input.strategyId,
      strategyVersionId: input.strategyVersionId,
      releaseId: input.releaseId,
    });
  }

  async list(projectId: string, signal?: AbortSignal): Promise<ExperimentDocument[]> {
    const response = await fetch(this.url(projectId), { headers: await getAuthHeaders(), signal });
    const payload = await handleResponse<{ experiments: ExperimentDocument[] }>(response);
    return payload.data.experiments;
  }

  async get(projectId: string, experimentId: string, signal?: AbortSignal): Promise<ExperimentDocument> {
    const response = await fetch(this.url(projectId, `/${encodeURIComponent(experimentId)}`), {
      headers: await getAuthHeaders(),
      signal,
    });
    const payload = await handleResponse<{ experiment: ExperimentDocument }>(response);
    return payload.data.experiment;
  }

  /**
   * FU part G (D-F4): owner-facing manual research report generation —
   * synchronous wrapper over the SAME engine+audit action as the admin CLI
   * route. Resolves once the report is persisted and the card upserted.
   */
  async generateResearchReport(projectId: string, input: {
    from?: string;
    to?: string;
    preset?: 'full' | 'acquisition' | 'funnel' | 'pricing';
    seriesId?: string;
  }): Promise<{ reportId: string; cardId: string; taskId: string; seriesId?: string }> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/insights/v1/research-reports`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(input),
      },
    );
    const payload = await handleResponse<{ reportId: string; cardId: string; taskId: string; seriesId?: string }>(response);
    return payload.data;
  }

  /**
   * E2c Phase B lazy detail: the stored ResearchReport JSON (backend gunzips
   * the private builds-bucket object). Render-only — the FE never computes.
   */
  async getResearchReport(projectId: string, reportId: string, signal?: AbortSignal): Promise<ResearchReport> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/insights/v1/research-reports/${encodeURIComponent(reportId)}`,
      { headers: await getAuthHeaders(), signal },
    );
    const payload = await handleResponse<{ report: ResearchReport }>(response);
    return payload.data.report;
  }

  async create(projectId: string, input: ExperimentInputPayload): Promise<ExperimentDocument> {
    const response = await fetch(this.url(projectId), {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    const payload = await handleResponse<{ experiment: ExperimentDocument }>(response);
    return payload.data.experiment;
  }

  async update(projectId: string, experimentId: string, input: Partial<ExperimentInputPayload>): Promise<ExperimentDocument> {
    const response = await fetch(this.url(projectId, `/${encodeURIComponent(experimentId)}`), {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    const payload = await handleResponse<{ experiment: ExperimentDocument }>(response);
    return payload.data.experiment;
  }

  async transition(projectId: string, experimentId: string, payload: ExperimentTransitionPayload): Promise<ExperimentDocument> {
    const response = await fetch(this.url(projectId, `/${encodeURIComponent(experimentId)}/transition`), {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const envelope = await handleResponse<{ experiment: ExperimentDocument }>(response);
    return envelope.data.experiment;
  }

  async getAuditLog(projectId: string, experimentId: string, signal?: AbortSignal): Promise<ExperimentAuditEntry[]> {
    const response = await fetch(this.url(projectId, `/${encodeURIComponent(experimentId)}/audit`), {
      headers: await getAuthHeaders(),
      signal,
    });
    const payload = await handleResponse<{ entries: ExperimentAuditEntry[] }>(response);
    return payload.data.entries;
  }

  async getAdvancedReadiness(
    projectId: string,
    experimentId: string,
    signal?: AbortSignal,
  ): Promise<AdvancedReadinessSafeRecord | null> {
    const response = await fetch(this.url(
      projectId,
      `/${encodeURIComponent(experimentId)}/readiness`,
    ), {
      headers: await getAuthHeaders(),
      signal,
    });
    const payload = await handleResponse<{ record: unknown }>(response);
    return parseAdvancedReadinessRecord(payload.data.record, { projectId, experimentId });
  }

  async getSegmentResponse(projectId: string, experimentId: string, signal?: AbortSignal): Promise<SegmentResponseMapData> {
    const response = await fetch(this.url(projectId, `/${encodeURIComponent(experimentId)}/segment-response`), {
      headers: await getAuthHeaders(),
      signal,
    });
    const payload = await handleResponse<SegmentResponseMapData>(response);
    return payload.data;
  }

  // ── Spec D4: candidate conveyor — these live on the CLI facade surface
  // (`/api/cli/v1/*`), NOT the insights/v1 experiments surface above. Firebase
  // user tokens are accepted there (InsightImpactDialog precedent).

  private cliUrl(projectId: string, suffix: string): string {
    return `${this.baseUrl}/api/cli/v1/projects/${encodeURIComponent(projectId)}${suffix}`;
  }

  async prepareCandidate(projectId: string, request: CliCandidatePrepareRequest): Promise<CliCandidatePrepareData> {
    const response = await fetch(this.cliUrl(projectId, '/experiments/candidate/prepare'), {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(request),
    });
    const payload = await handleResponse<CliCandidatePrepareData>(response);
    return payload.data;
  }

  async getCandidate(projectId: string, experimentId: string, signal?: AbortSignal): Promise<CliCandidateStatusData> {
    const response = await fetch(this.cliUrl(projectId, `/experiments/${encodeURIComponent(experimentId)}/candidate`), {
      headers: await getAuthHeaders(),
      signal,
    });
    const payload = await handleResponse<CliCandidateStatusData>(response);
    return payload.data;
  }

  async discardCandidate(projectId: string, experimentId: string): Promise<CliCandidateStatusData> {
    const response = await fetch(this.cliUrl(projectId, `/experiments/${encodeURIComponent(experimentId)}/candidate/discard`), {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({}),
    });
    const payload = await handleResponse<CliCandidateStatusData>(response);
    return payload.data;
  }

  /** G2 exposure guard (P3): resolves fresh, or throws with `code: 'candidate_stale'` when insights moved on. */
  async confirmCandidateExposure(projectId: string, experimentId: string): Promise<{ experimentId: string; fresh: true }> {
    const response = await fetch(this.cliUrl(projectId, `/experiments/${encodeURIComponent(experimentId)}/candidate/confirm-exposure`), {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({}),
    });
    const payload = await handleResponse<{ experimentId: string; fresh: true }>(response);
    return payload.data;
  }

  /** Lineage chain for the client-side staleness hint (render-time G2 check). */
  async getInsightLineage(projectId: string, analysisId: string, signal?: AbortSignal): Promise<CliProductInsightLineageData> {
    const response = await fetch(this.cliUrl(projectId, `/product/insights/${encodeURIComponent(analysisId)}/lineage`), {
      headers: await getAuthHeaders(),
      signal,
    });
    const payload = await handleResponse<CliProductInsightLineageData>(response);
    return payload.data;
  }
}
